import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';

export interface TenantDto {
  id: string;
  name: string;
  active: boolean;
  logo?: string;
  mainColor?: string;
  secondaryColor?: string;
  /** {@code true} = login via SSO; {@code false} = login via user/password. */
  useSso?: boolean;
  maintenancePct?: number;
  /** When true, only emails matching {@link allowedEmailDomains} may register. */
  restrictEmailDomain?: boolean;
  /** Domain suffixes (e.g. atos.net) when {@link restrictEmailDomain} is true. */
  allowedEmailDomains?: string[];
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface TenantCreateRequest {
  id: string;
  name: string;
  logo?: string;
  mainColor?: string;
  secondaryColor?: string;
  useSso?: boolean;
  maintenancePct?: number;
  restrictEmailDomain?: boolean;
  allowedEmailDomains?: string[];
}

export interface TenantUpdateRequest {
  name: string;
  active: boolean;
  logo?: string;
  mainColor?: string;
  secondaryColor?: string;
  useSso?: boolean;
  maintenancePct?: number;
  restrictEmailDomain?: boolean;
  allowedEmailDomains?: string[];
}

@Injectable({ providedIn: 'root' })
export class TenantsService {

  private readonly baseUrl = environment.tenantsUrl;
  /**
   * Cached tenant returned by {@link #getCurrent}, kept so guards and the
   * authentication redirect can ask for it on every navigation without
   * triggering an HTTP call each time. Invalidated whenever the authenticated
   * user changes (login / logout / different user) so a tenant from a previous
   * session can never leak into the current one.
   */
  private cachedTenant: TenantDto | null = null;
  private cachedForUserId: string | null = null;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.authService.getUserData().subscribe((user: any) => {
      const userId: string | null = user?.id ?? null;
      if (userId !== this.cachedForUserId) {
        this.cachedForUserId = userId;
        this.cachedTenant = null;
      }
    });
  }

  private getHeaders(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.authService.getToken()
      })
    };
  }

  list(activeOnly: boolean): Observable<TenantDto[]> {
    const q = activeOnly ? '?activeOnly=true' : '?activeOnly=false';
    return this.http.get<TenantDto[]>(this.baseUrl + q, this.getHeaders());
  }

  /** Fetches the caller's tenant from the backend and updates the cache. */
  getCurrent(): Observable<TenantDto> {
    return this.http.get<TenantDto>(this.baseUrl + '/current', this.getHeaders()).pipe(
      tap(t => { this.cachedTenant = t ?? null; })
    );
  }

  /**
   * Returns the cached current tenant if available; otherwise fetches it once.
   * Use this from guards / chrome components to avoid hammering the backend on
   * every navigation.
   */
  loadCurrent(): Observable<TenantDto | null> {
    return this.cachedTenant ? of(this.cachedTenant) : this.getCurrent();
  }

  create(body: TenantCreateRequest): Observable<TenantDto> {
    return this.http.post<TenantDto>(this.baseUrl, body, this.getHeaders());
  }

  update(id: string, body: TenantUpdateRequest): Observable<TenantDto> {
    return this.http.put<TenantDto>(this.baseUrl + '/' + encodeURIComponent(id), body, this.getHeaders()).pipe(
      tap(t => {
        if (this.cachedTenant?.id === t?.id) {
          this.cachedTenant = t;
        }
      })
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + '/' + encodeURIComponent(id), this.getHeaders());
  }
}
